<template>
  <div class="flex flex-col items-center justify-center py-12 px-6 max-w-xl mx-auto text-center">
    <!-- Icon and Header -->
    <div class="mb-8">
      <v-icon size="64" class="text-blue-400 mb-4">mdi-file-document-multiple-outline</v-icon>
      <h1 class="text-2xl font-medium mb-2">Duplicate Entity CSV Download</h1>
      <p class="text-gray-400">
        Generate and download a CSV file containing all duplicate entities.
      </p>
    </div>

    <!-- Last Modified Date -->
    <div v-if="lastModified" class="mb-4 text-gray-500">
      <v-icon size="small" class="mr-1 align-middle">mdi-calendar-clock</v-icon>
      <span>Last updated: {{ formattedLastModified }}</span>
    </div>

    <!-- Download Button -->
    <v-btn
      :loading="downloadLoading"
      :disabled="downloadLoading"
      size="large"
      color="light blue"
      class="mb-6 px-8 py-6 font-medium"
      prepend-icon="mdi-download"
      elevation="3"
      @click="downloadData"
    >
      <template v-if="downloadLoading">
        <v-icon left class="mr-2 animate-spin">mdi-loading</v-icon>
        DOWNLOADING...
      </template>
      <template v-else>
        DOWNLOAD DUPLICATE ENTITY CSV
      </template>
    </v-btn>

    <!-- Status Messages -->
    <transition name="fade">
      <div
        v-if="error"
        class="bg-opacity-10 bg-red-500 border-l-4 border-red-500 p-4 mb-4 w-full max-w-md text-left"
      >
        <div class="flex">
          <v-icon color="error" class="mr-3">mdi-alert-circle</v-icon>
          <p class="text-red-400 text-sm">{{ error }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import HTTP from '@/api/owl';

export default {
  data() {
    return {
      initialLoading: true,
      downloadLoading: false,
      error: null,
      lastModified: null,
      encodedData: null,
    };
  },
  computed: {
    formattedLastModified() {
      if (!this.lastModified) return '';

      // Format the date in a user-friendly way
      const date = new Date(this.lastModified);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },
  },
  mounted() {
    this.fetchMetadata();
  },
  methods: {
    fetchMetadata() {
      this.initialLoading = true;
      this.error = null;

      HTTP.get(`/admin/duplicate_entity/download`)
        .then(response => {
          this.lastModified = response.data.last_modified;
          this.encodedData = response.data.encoded_string;
          this.initialLoading = false;
        })
        .catch(err => {
          this.error = `Failed to fetch file metadata: ${err.toString()}`;
          this.$flash('error', this.error);
          this.initialLoading = false;
        });
    },

    downloadData() {
      this.downloadLoading = true;
      this.error = null;

      // If we already have the data, download it immediately
      if (this.encodedData) {
        this.decodeAndDownload(this.encodedData);
        return;
      }

      // Otherwise fetch the data first
      HTTP.get(`/admin/duplicate_entity/download`)
        .then(response => {
          this.encodedData = response.data.encoded_string;
          this.lastModified = response.data.last_modified;
          this.decodeAndDownload(response.data.encoded_string);
        })
        .catch(err => {
          this.error = err.toString();
          this.$flash('error', err.toString());
        })
        .finally(() => {
          this.downloadLoading = false;
        });
    },

    decodeAndDownload(encodedData) {
      try {
        // Decode the base64 string
        const decodedData = atob(encodedData);
        const blob = new Blob([decodedData], { type: 'text/csv' });

        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'duplicate_entities.csv');

        // Append to the document, click, and remove
        document.body.appendChild(link);
        link.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);

        this.$flash('success', 'File downloaded successfully');
        this.downloadLoading = false;
      } catch (e) {
        this.error = e.toString();
        this.$flash('error', 'Failed to process the download');
        this.downloadLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
